import { useQuery } from '@tanstack/react-query';
import { API, Auth } from 'aws-amplify';
import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/auth-context';
import {
  useAllianceIdentity,
  useAuthorizeQuery,
} from '../hooks/use-alliance-identity';
import { usePermissions } from '../hooks/use-permissions';
import { useSessionActions } from '../hooks/use-session-actions';
import { AuthorizeStatus, AWS_GROUP, BillingPermissionData } from '../types';
import { captureError } from '../utils/capture-error';
import { checkForRedirectPath, storeRedirectPath } from '../utils/redirect';
import { AgreementAlert } from './agreement-alert';
import { BillingSetupBanner, formatAllianceDate } from './alliance/app-banners';
import { SystemIdentity, SystemSplash } from './alliance/system-screen';
import {
  AccessDeniedScreen,
  AccountInactiveScreen,
  AccountPendingScreen,
  AuthFailScreen,
  MaintenanceScreen,
  MfaRequiredScreen,
  NotAuthorizedScreen,
  SessionExpiredScreen,
} from './alliance/system-screens';
import { GateFrame } from './gate-frame';

/** The shape of an Amplify API (axios) error, as far as the gate needs it */
interface ApiError {
  message?: string;
  response?: {
    status?: number;
    data?: {
      error?: string;
      message?: string;
      errors?: { msg?: string | null }[];
      data?: { UserStatus?: string };
    };
  };
}

/**
 * True when an error means the Cognito session is gone (refresh token
 * expired or revoked). Amplify sometimes rejects with a plain string.
 */
const isSessionError = (error: unknown) => {
  const err = error as (ApiError & { code?: string; name?: string }) | null;
  if (err?.response?.status === 401) {
    return true;
  }
  const text = `${err?.code || ''} ${err?.name || ''} ${
    err?.message || (typeof error === 'string' ? error : '')
  }`;
  return /not authenticated|no current user|NotAuthorizedException|refresh token|token has expired|token has been revoked/i.test(
    text,
  );
};

/** The API's own message from an error response, when it sent one */
const getServerMessage = (error: unknown) => {
  const data = (error as ApiError | null)?.response?.data;
  return (
    (typeof data?.error === 'string' ? data.error : null) ||
    data?.errors?.find((item) => item.msg)?.msg ||
    data?.message ||
    null
  );
};

/**
 * The gate in front of the signed-in shell. In order:
 *
 * 1. Maintenance (agents only; admins get the banner in the shell)
 * 2. Splash while the session, both /authorize calls, ICA status and MFA
 *    checks load. Billing permissions load in the background; the billing
 *    routes wait for them (AuthContext gettingPermissions)
 * 3. Session expired, when Cognito says the session is gone. Checked before
 *    MFA, so a dead session never lands on the MFA screen
 * 4. The MFA check failed for another reason: couldn't load (not "no MFA")
 * 5. MFA not set up: the MFA screen, which sends them to Contracting
 * 6. The annual ICA agreement alert (unchanged)
 * 7. Dashboard API /authorize (production builds only, as before): 403
 *    Pending / Inactive / Terminated → that screen, any other 403 → not
 *    authorized, any other error → couldn't load with the API message
 * 8. An inactive employee record (Contracting API /authorize Staff.Active
 *    '0'): inactive, as in Contracting and Leads
 * 9. The app, plus the "billing setup incomplete" banner for ARC_BillingSystem
 *    users the Billing API doesn't know yet
 *
 * Otherwise the Contracting API /authorize (useAuthorizeQuery) only names
 * the user on these screens: if it fails, nobody is blocked.
 */
export function RequireAuth(props: {
  children: JSX.Element;
  isAdmin: boolean;
  maintData:
    | {
        maintenanceActive: boolean;
        maintenanceBegin: string;
        maintenanceEnd: string;
      }
    | null
    | undefined;
}) {
  // Context
  const { state, dispatch } = React.useContext(AuthContext);
  // State
  const [dismissICA, setDismissICA] = React.useState(false);
  const [fromCognito] = React.useState(() =>
    new URLSearchParams(window.location.search).has('code'),
  );
  // Hooks
  const navigate = useNavigate();
  const { hasGroup } = usePermissions();
  const identity = useAllianceIdentity();
  const queryIdentity = useAuthorizeQuery();
  const { signOut, signInAgain } = useSessionActions();

  React.useEffect(() => {
    const handleCheckForPath = async () => {
      try {
        const redirectPath = await checkForRedirectPath();
        if (redirectPath) {
          navigate(redirectPath);
        }
      } catch (error) {
        captureError({ data: { error } });
      }
    };

    const handleStorePath = async () => {
      try {
        if (window?.location?.pathname) {
          const { pathname } = window.location;
          // If the user was trying to access a route other than the main route
          if (pathname !== '/') {
            let path = pathname;
            // If there are any search parameters add them to the redirect path
            if (window?.location?.search) {
              path = path + window.location.search;
            }
            // Add the route to storage for use after logging in
            await storeRedirectPath({ pathname: path });
          }
        }
      } catch (error) {
        captureError({ data: { error } });
      }
    };

    if (state.user !== undefined) {
      // Check for stored path
      handleCheckForPath();
    } else {
      // Store path
      handleStorePath();
    }
  }, [state.user, navigate, dispatch]);

  const isAuthenticated = state.user !== undefined;

  // Query - Authorize User (Dashboard API; decides access by UserStatus)
  const pathAuth = '/authorize';
  const queryAuth = useQuery({
    enabled: isAuthenticated && process.env.REACT_APP_AWS_BRANCH === 'master',
    queryKey: [pathAuth],
    queryFn: async () => {
      const response: {
        data: { LastAccessTime: string };
      } = await API.post('DashboardAPI', pathAuth, {});

      return response.data;
    },
    onError: (error: any) => {
      // Only capture Errors that don't have a status of 403
      if (error?.response?.status !== 403) {
        captureError({ data: { error } });
      }
    },
  });

  // Query - Check for Signed ICA and verified NPN
  const pathStatus = '/authorize/status';
  const queryStatus = useQuery({
    enabled: isAuthenticated,
    queryKey: [pathStatus],
    queryFn: async () => {
      const response: {
        data: AuthorizeStatus | null;
      } = await API.post('DashboardAPI', pathStatus, {});

      if (response.data) {
        dispatch({ type: 'SET_STATUS', payload: response.data });
      }

      return response.data;
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Query - Check that the user has set up Multi-Factor Authentication for their account
  const queryMFA = useQuery({
    enabled: isAuthenticated,
    queryKey: ['check_Auth.getPreferredMFA'],
    queryFn: async () => {
      // Bypass Cache to always get the latest preferences for the Current User
      const currentUser = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      const preferredMFA = await Auth.getPreferredMFA(currentUser);

      const mfaOptions = ['EMAIL_OTP', 'SMS_MFA', 'SOFTWARE_TOKEN_MFA'];

      // Determine if the user has selected one of the available options
      return mfaOptions.includes(preferredMFA || '');
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Query - Billing Permissions
  const pathBilling = '/access/user-permissions';
  const queryBilling = useQuery({
    enabled: isAuthenticated && hasGroup(AWS_GROUP.ARC_BillingSystem),
    queryKey: [pathBilling],
    queryFn: async () => {
      const response: {
        data: BillingPermissionData[];
      } = await API.post('BillingAPI', pathBilling, {});

      if (response.data.length) {
        dispatch({ type: 'SET_BILLING_PERM', payload: response.data });
      } else {
        dispatch({ type: 'SET_BILLING_PERM', payload: [] });
      }

      return response.data;
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Not all users will have Billing permissions
  // We need to check the fetch status to see if the query is actually running
  const isLoadingBilling =
    queryBilling.isLoading && queryBilling.fetchStatus !== 'idle';

  React.useEffect(() => {
    // The app doesn't wait for billing permissions (the Billing API can be
    // slow); the billing routes wait for them instead
    dispatch({ type: 'SET_BILLING_GET', payload: isLoadingBilling });
  }, [dispatch, isLoadingBilling]);

  // We need to check the fetch status to see if the query is actually running
  const isLoadingICA =
    queryStatus.isLoading && queryStatus.fetchStatus !== 'idle';

  const isLoadingMFA = queryMFA.isLoading && queryMFA.fetchStatus !== 'idle';

  const isAuthorizing = queryAuth.isLoading && queryAuth.fetchStatus !== 'idle';

  // The Contracting /authorize can block an inactive employee, so wait for it
  // too (a failure doesn't block)
  const isIdentifying =
    queryIdentity.isLoading && queryIdentity.fetchStatus !== 'idle';

  // "Signed in as" on the full-page screens: name + number once the
  // Contracting /authorize has answered, otherwise the Cognito username
  let systemIdentity: SystemIdentity | null = null;
  if (identity && queryIdentity.isSuccess) {
    systemIdentity = {
      kind: 'full',
      name: identity.user.fullName,
      id: identity.user.id,
    };
  } else if (state.user) {
    systemIdentity = { kind: 'token', id: state.user.getUsername() };
  }

  const handleRetry = () => {
    queryAuth.refetch();
    queryMFA.refetch();
  };

  const authError = queryAuth.error as ApiError | null;
  const isForbidden = queryAuth.isError && authError?.response?.status === 403;
  const userStatus = authError?.response?.data?.data?.UserStatus;

  const sessionExpired =
    (queryMFA.isError && isSessionError(queryMFA.error)) ||
    (queryAuth.isError && isSessionError(queryAuth.error)) ||
    (state.tokenError && (queryAuth.isError || queryMFA.isError));

  // It is possible for a user to be given the Cognito Billing Permission
  // but they have not yet been added as a User in the Billing System
  const alertBillingPermissions =
    queryBilling.isError &&
    (queryBilling.error as ApiError | null)?.response?.status === 403;

  let screen: JSX.Element | null = null;
  if (isAuthenticated && state.signingOut) {
    // Hold here until Cognito has signed out (then USER_CLEAR → Sign In)
    screen = <SystemSplash caption="Signing you out…" />;
  } else if (
    !props.isAdmin &&
    props.maintData &&
    props.maintData.maintenanceActive
  ) {
    // Display a Maintenance Message to agents
    screen = (
      <MaintenanceScreen
        until={formatAllianceDate({
          value: props.maintData.maintenanceEnd,
          long: true,
        })}
        identity={systemIdentity}
        onSignOut={isAuthenticated ? signOut : undefined}
      />
    );
  } else if (
    state.isChecking ||
    isAuthorizing ||
    isIdentifying ||
    isLoadingICA ||
    isLoadingMFA
  ) {
    screen = (
      <SystemSplash caption={fromCognito ? 'Signing you in…' : undefined} />
    );
  } else if (isAuthenticated && sessionExpired) {
    screen = <SessionExpiredScreen onSignIn={signInAgain} />;
  } else if (isAuthenticated && queryMFA.isError) {
    // The MFA check failed for another reason: that isn't "no MFA"
    screen = (
      <AuthFailScreen
        identity={systemIdentity}
        onRetry={handleRetry}
        onSignOut={signOut}
      />
    );
  } else if (isAuthenticated && queryMFA.isSuccess && !queryMFA.data) {
    screen = (
      <MfaRequiredScreen identity={systemIdentity} onSignOut={signOut} />
    );
  } else if (
    queryStatus.data &&
    queryStatus.data.HasSignedICA === false &&
    !dismissICA
  ) {
    return <AgreementAlert onDismiss={() => setDismissICA(true)} />;
  } else if (isForbidden && userStatus === 'Pending') {
    screen = (
      <AccountPendingScreen identity={systemIdentity} onSignOut={signOut} />
    );
  } else if (isForbidden && userStatus === 'Inactive') {
    screen = (
      <AccountInactiveScreen identity={systemIdentity} onSignOut={signOut} />
    );
  } else if (isForbidden && userStatus === 'Terminated') {
    screen = <AccessDeniedScreen onSignOut={signOut} />;
  } else if (isForbidden) {
    screen = (
      <NotAuthorizedScreen identity={systemIdentity} onSignOut={signOut} />
    );
  } else if (queryAuth.isError) {
    screen = (
      <AuthFailScreen
        identity={systemIdentity}
        onRetry={handleRetry}
        onSignOut={signOut}
        serverMessage={getServerMessage(queryAuth.error)}
      />
    );
  } else if (queryIdentity.data?.Staff?.Active === '0') {
    // An inactive employee (admin) record
    screen = (
      <AccountInactiveScreen identity={systemIdentity} onSignOut={signOut} />
    );
  } else if (isAuthenticated) {
    return (
      <React.Fragment>
        {alertBillingPermissions ? <BillingSetupBanner /> : null}

        {props.children}
      </React.Fragment>
    );
  } else {
    return <Navigate to="/sign-in" replace />;
  }

  return <GateFrame>{screen}</GateFrame>;
}
